import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import classNames from "classnames";
import invariant from "invariant";

import { useCollapseContext } from "./Collapse";
import useWindowResizedEffect from "./useWindowResizedEffect";
import dispatchEvent from "./dispatchEvent";
import { assignForwardedRef } from "./assignForwardedRef";

const CollapseHiddenContentContext = createContext({});

export const useCollapseHiddenContentContext = () => {
  const context = useContext(CollapseHiddenContentContext);

  return context || { isHidden: false };
};

// This is an accessible collapsible div container that always render its children, but
// hides them if the `in` prop is `false`, and shows them if it's `true`, with a 300ms css
// transition that looks like classic accordion and collapse plugins.
//
// It can easily be expanded and collapsed from a <CollapseSwitch> component, as they're
// built to work together.
//
// Note that this component must be used as a child of a <Collapse> component, or it will
// throw an error.
//
//
const CollapseContent = props => {
  const { children, ref: forwardedRef } = props;
  const [container, setContainer] = useState();
  const [maxHeight, setMaxHeight] = useState(0);
  const [hiddenRendererContainer, setHiddenRendererContainer] = useState();

  const collapseProps = useCollapseContext() || {};
  const collapseId =
    typeof props.collapseId !== "undefined" ? props.collapseId : collapseProps.collapseId;
  const isExpanded =
    typeof props.isExpanded !== "undefined" ? props.isExpanded : collapseProps.isExpanded;
  const wasExpandedRef = useRef(isExpanded);

  const [displayContentOverflow, setDisplayContentOverflow] = useState(isExpanded);
  const displayContentOverflowTimerId = useRef();

  invariant(
    collapseId,
    "The <CollapseContent> component must be used as a child of the <Collapse> component"
  );

  const setContainerRef = useCallback(
    node => {
      setContainer(node);
      assignForwardedRef(forwardedRef, node);
    },
    [forwardedRef]
  );

  const recalculateContainerHeight = useCallback(() => {
    if (!container || !hiddenRendererContainer) return;

    // If something changed in the hidden renderer container, we need to recalculate the
    // total height of the inner content to set the correct max-height style on the main
    // container.
    const containerInnerHeight = Array.from(container.childNodes).reduce(
      (height, node) => height + node.offsetHeight,
      0
    );

    // Note : `scrollHeight` returns the height of the element contents, even if it's
    // not displayed when we query it.
    const targetHeight = isExpanded
      ? Math.max(hiddenRendererContainer.offsetHeight, containerInnerHeight)
      : 0;
    // Update the target height if needed, the css transition on the max-height prop will
    // animate the transition between the two values.
    if (targetHeight !== maxHeight) setMaxHeight(targetHeight);
  }, [container, hiddenRendererContainer, isExpanded, maxHeight]);

  useEffect(recalculateContainerHeight, [recalculateContainerHeight]);
  useWindowResizedEffect(recalculateContainerHeight, [recalculateContainerHeight]);

  // Ensure that new loaded content inside the collapsible container triggers a height
  // recalculation to adjust the max-height style accordingly. This is needed if there's
  // some kind of collapsible content inside the CollapseContent that may load new
  // elements while the panel is expanded.
  useEffect(() => {
    if (!container) return;

    container.addEventListener("newContentLoaded", recalculateContainerHeight);

    return () => {
      container.removeEventListener("newContentLoaded", recalculateContainerHeight);
    };
  }, [container, recalculateContainerHeight]);

  // This effect hook allows setting whether the overflowing content of the collapsible
  // panel is shown or not. This is needed because :
  //
  //   1. To create the show and hide sliding animations, we need to hide the overflowing
  //      content to look nice.
  //   2. If the content of the collapsible content contains dropdowns or other elements
  //      that may overflow the panel inner content size, we need to let them be displayed
  //      without being cut out (collapsible filter panels requires that)
  //
  useEffect(() => {
    // If the expanded state was not changed during this render, don't do anything as it
    // should already be in the right state.
    if (wasExpandedRef.current === isExpanded) return;
    // Immediately update the ref to ensure that the next render won't try to update the
    // overflow display state once again.
    wasExpandedRef.current = isExpanded;

    // If a timer was already running to display or hide overflowing content, which can
    // happen if the user opens and closes the collapse quickly, we ensure that we clear
    // the timer before starting the next, to avoid overflowing content flashes and state
    // desynchronisation.
    if (displayContentOverflowTimerId.current) {
      clearTimeout(displayContentOverflowTimerId.current);
    }

    const overflowDelay = isExpanded ? 300 : 0;

    // Depending on the new expanded state, we decide to show or hide the overflowing
    // content.
    //
    // Note that to let the animation run smoothly, we need to :
    //
    //   - Display overflowing content *after* the panel animation is complete
    //   - Hide overflowing content *before* the panel animation starts.
    //
    displayContentOverflowTimerId.current = setTimeout(() => {
      setDisplayContentOverflow(isExpanded);
      // Also immediately dispatch a "newContentLoaded" event to ensure that the parent
      // collapse recalculates its height right away when opening, as there may be new
      // visible content to take into account.
      dispatchEvent(container, "newContentLoaded");
    }, overflowDelay);

    return () => {
      // Also ensure that the timeout is cleared when the component is unmounted
      if (displayContentOverflowTimerId.current) {
        clearTimeout(displayContentOverflowTimerId.current);
      }
    };
  }, [container, displayContentOverflow, isExpanded]);

  // We use the "collapse-content" class as base of our collapse mechanism setup.
  const className = classNames("collapse-content", props.className, {
    "collapse-content-shown": isExpanded,
    "collapse-content-hidden": !isExpanded,
    invisible: !isExpanded,
    "overflow-visible": displayContentOverflow
  });

  return (
    <div className="position-relative">
      {/* 
        This container is meant to render the collapse contents to define the final height 
        required when displaying the collapse contents, and allow our max-height style to
        be animated properly
      */}
      <div
        className="collapse-content-hidden-renderer-container"
        ref={setHiddenRendererContainer}
        inert
        aria-hidden="true"
      >
        <CollapseHiddenContentContext.Provider value={{ isHidden: true }}>
          {children}
        </CollapseHiddenContentContext.Provider>
      </div>

      <div
        id={collapseId}
        className={className}
        style={{ maxHeight }}
        ref={setContainerRef}
        role="region"
        inert={isExpanded ? null : true}
        // We use `displayContentOverflow` instead of `isExpanded` here, as there seem to
        // be an issue when using `isExpanded` where the attribute is reset to "true"
        // after the transition to open the collapse ends.
        aria-hidden={displayContentOverflow ? null : "true"}
      >
        {children}
      </div>
    </div>
  );
};

export default CollapseContent;
